import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useGetMemberListDataQuery,
  useGetSurveyByChapterIdQuery,
  useGetSurveyByUserIdQuery,
  useGetSurveyNumericDataQuery,
} from 'store/services/feedbackGroup';
import DividerWithText from 'style-guide/DividerWithText';
import Pagination from 'style-guide/Pagination';
import Skeleton from 'style-guide/Skeleton';
import Table from 'style-guide/Table';
import Text from 'style-guide/Typography/Text';
import ChartComponent from './Chart';
import { hasAnyAnswer } from './helper';
import { memberListTableColumns, numericTableColumns } from './tableColumn';

const ChapterChartComponent = ({ chapterId, maxPoint, singleChapter }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [memberPage, setMemberPage] = useState(1);
  const chartRef = useRef(null);

  const param = searchParams.get('question') || '';
  const memberParam = searchParams.get('member') || '';
  const userIdParam = searchParams.get('userId') || '';

  const { data, isFetching } = useGetSurveyByChapterIdQuery({ chapterId });
  const { data: numericData } = useGetSurveyNumericDataQuery(
    { chapterId, question: param, page },
    { refetchOnMountOrArgChange: true }
  );
  const { data: memberListData } = useGetMemberListDataQuery(
    { chapterId, page: memberPage, member: memberParam },
    { refetchOnMountOrArgChange: true }
  );

  const { data: surveyData, isFetching: isFetchingSurvey } = useGetSurveyByUserIdQuery(
    {
      userId: userIdParam,
      chapterId: +chapterId,
    },
    { skip: !userIdParam || !chapterId }
  );

  const result = surveyData?.[0]?.feedbackGroups.flatMap(({ legend, feedbackQuestions }) =>
    feedbackQuestions
      .filter(({ surveyAnswers }) => surveyAnswers.length > 0)
      .map(({ question, surveyAnswers }) => ({
        averagePoint: surveyAnswers[0].point,
        legend,
        question,
      }))
  );

  const feedbackGroupData = userIdParam ? surveyData?.[0]?.feedbackGroups : data?.feedbackGroups;

  const showRadarChart = hasAnyAnswer(feedbackGroupData);

  useEffect(() => {
    chartRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [userIdParam]);

  if (isFetching || isFetchingSurvey) {
    return (
      <div className='d-flex justify-content-center mt--24'>
        <Skeleton skeleton={{ height: 400, width: 800 }} />
      </div>
    );
  }

  if (!data?.feedbackGroups || data.feedbackGroups.length === 0) {
    return (
      <div className={`${singleChapter ? 'mt--96' : 'mt--24'} text-align-center`}>
        <Text>This {singleChapter ? 'course' : 'chapter'} does not have any survey attached.</Text>
        <DividerWithText />
      </div>
    );
  }

  return (
    <div className='chapter-chart' ref={chartRef}>
      <div className='d-flex justify-content-center'>
        <ChartComponent feedbackGroups={feedbackGroupData} maxPoint={maxPoint} showRadarChart={showRadarChart} />
      </div>
      {showRadarChart && (
        <div className='mt--48'>
          <Text>Survey numeric table:</Text>
          <Table
            columns={numericTableColumns()}
            rowKey={(e) => e.key}
            data={userIdParam ? result : numericData?.row}
            className='mt--16'
          />
          <div className='mt--24'>
            <Pagination total={numericData?.count} current={page} onChange={setPage} className='pagination' />
          </div>
        </div>
      )}
      {userIdParam || !showRadarChart ? null : (
        <div className='mt--48'>
          <Text>Member List table:</Text>
          <Table
            columns={memberListTableColumns(setSearchParams)}
            rowKey={(e) => e.key}
            data={memberListData?.row}
            className='mt--16'
          />
          <div className='mt--24'>
            <Pagination
              total={memberListData?.count}
              current={memberPage}
              onChange={setMemberPage}
              className='pagination'
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChapterChartComponent;
